import "./orders-history.scss";

import classNames from "classnames";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useDebounce } from "react-use";

import {
	NewOrderNotification,
	NoOrdersBasedOnFilters,
	OrderCardDetails,
	OrderCardHeader,
} from "@client/components";
import {
	DropDown,
	DropDownOption,
} from "@client/components/reusable/drop-down";
import { getTotalNumberOfProducts } from "@client/components/reusable/hooks/useGetTotalNumberOfProducts";
import { Loading } from "@client/components/reusable/loading/loading";
import SvgIcon from "@client/components/reusable/SvgIcons";
import { ReactComponent as ClearBtn } from "@client/components/reusable/SvgIcons/library/clear_btn.svg";
import { ReactComponent as SearchIcon } from "@client/components/reusable/SvgIcons/library/search_icon2.svg";
import { SendHistoryReportButton } from "@client/components/send-mail/send-history-report";
import { useRootContext } from "@client/context/RootContext";
import {
	Order,
	OrdersHistoryStatuses,
	OrdersHistoryTimeframe,
} from "@client/stores";
import { OrderStatuses } from "@client/stores/vendor-store";

const customStatusLabels = [
	{ status: OrderStatuses.Timedout, label: "Timed out" },
];
export const orderStatusToLabel = (status: OrderStatuses) => {
	const hasCustom = customStatusLabels.find((el) => el.status == status);
	return hasCustom ? hasCustom.label : status;
};

function formatDeliveryDate(date: string) {
	return dayjs(date).format("DD/MM/YYYY");
}
export const OrdersHistory = observer(() => {
	const {
		vendorStore,
		vendorStore: { historyOrders, pullingOrdersHistoryInProgress },
		printStore: { printShippingLabel },
		layoutStore: { isNewOrder },

		modalStore,
	} = useRootContext();

	async function getHistoryOrdersData() {
		if (!selectedStatuses.length) {
			vendorStore.clearOrdersHistory();
			setCurrentPage(1);

			return;
		}

		await vendorStore.getVendorAndOrdersHistory(
			currentPage,
			timeFrameValue,
			selectedStatuses as typeof OrdersHistoryStatuses,
			debouncedSearchValue
		);
	}

	const initiallySelectedStatuses = useRef(
		localStorage.getItem("selectedStatuses")
			? (JSON.parse(
					localStorage.getItem("selectedStatuses")!
			  ) as typeof OrdersHistoryStatuses)
			: OrdersHistoryStatuses
	);

	const initiallySelectedTimeframe = useRef(
		localStorage.getItem("timeFrameValue")
			? Number(localStorage.getItem("timeFrameValue")!)
			: 0
	);

	const [selectedStatuses, setSelectedStatuses] = useState<DropDownOption[]>(
		initiallySelectedStatuses.current
	);
	const [timeFrameValue, setTimeFrameValue] = useState(
		initiallySelectedTimeframe.current
	);

	const [searchValue, setSearchValue] = useState("");
	const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

	const [isSearchInputOnFocus, setIsSearchInputOnFocus] = useState(false);
	const orderCardRef = useRef<HTMLDivElement>(null);

	const [currentPage, setCurrentPage] = useState(1);

	const [, cancel] = useDebounce(
		() => setDebouncedSearchValue(searchValue),
		1000,
		[searchValue]
	);

	useEffect(() => {
		setCurrentPage(1);

		getHistoryOrdersData();
	}, [timeFrameValue, selectedStatuses, debouncedSearchValue]);
	useEffect(() => {
		// we don't want to reset the pagination when changing page
		getHistoryOrdersData();
	}, [currentPage]);

	async function setSelectedStatuses_handler(state: DropDownOption[]) {
		localStorage.setItem("selectedStatuses", JSON.stringify(state));

		setSelectedStatuses(state as typeof OrdersHistoryStatuses);
	}
	async function setTimeFrameValue_handler(id: number) {
		setTimeFrameValue(id);
		localStorage.setItem("timeFrameValue", String(id));
	}
	async function setSearchValue_handler(
		e: React.ChangeEvent<HTMLInputElement>
	) {
		setSearchValue(e.currentTarget.value);
	}

	function clearSearchInput() {
		setSearchValue("");
	}

	// TODO refactor date
	function getFormatedDate(date: string) {
		const localDate = new Date(date);
		const dayMonthYear = dayjs(localDate).format("DD/MM/YYYY");
		const hourMinute = dayjs(localDate).format("HH:mm");
		return (
			<span>
				{dayMonthYear}
				<br />
				{hourMinute}
			</span>
		);
	}

	function handlePrintBtn(e: MouseEvent, order: Order) {
		e.preventDefault();
		e.stopPropagation();

		const reference = order.order_meta?.reference || String(order.id);
		printShippingLabel(reference);
	}

	function closeOrderCardModal() {
		modalStore.removeModal("order-card-modal");
	}

	function openOrderCardModal(e: MouseEvent, order: Order) {
		e.preventDefault();
		e.stopPropagation();

		modalStore.addModal({
			id: "order-card-modal",
			showCloseBtn: true,
			content: (
				<div className="order-card-modal-container">
					<OrderCardHeader
						order={order}
						closeOrderCardModal={closeOrderCardModal}
						isHistorySection
					/>
					<OrderCardDetails
						order={order}
						orderCardRef={orderCardRef}
						closeOrderCardModal={closeOrderCardModal}
						isOrdersHistory
					/>
				</div>
			),
		});
	}

	const sortedOrders = historyOrders;
	// const sortedOrders = useMemo(
	// 	() =>
	// 		historyOrders?.filter((order) =>
	// 			`${order.id}-${order.order_meta.magentoSaleId}-${
	// 				order.trackingNumber?.toLocaleLowerCase() || ""
	// 			}-${order.shipping_details.user_details.firstName?.toLocaleLowerCase()} ${order.shipping_details.user_details.lastName?.toLocaleLowerCase()}-${
	// 				order.products?.find(
	// 					(product) =>
	// 						product.order_details?.qtyCanceled &&
	// 						order.status !== "Rejected" &&
	// 						order.status !== "Timedout"
	// 				)
	// 					? "pk"
	// 					: ""
	// 			}`.includes(searchValue.toLocaleLowerCase())
	// 		),
	// 	[searchValue]
	// );

	function isPartialPickup(order: Order) {
		return order.products.find(
			(product) =>
				product.order_details?.qtyCanceled &&
				order.status !== "Rejected" &&
				order.status !== "Timedout"
		);
	}

	const visibleOrders = searchValue
		? sortedOrders?.slice()
		: historyOrders?.slice();

	const headerOptions = [
		"Date",
		"Order Number",
		"TX Number",
		"Client Name",
		"Items",
		"Tracking Number",
		"Status",
		`SCHEDULED DELIVERY DATE`,
		`ACTUAL DELIVERY DATE`,
		"",
		" ",
	];

	return (
		<div className="order-history-wrapper">
			{isNewOrder && (
				<NewOrderNotification
					notificationMessage="You have new orders!"
					buttonText="Active Orders"
					link="/"
				/>
			)}
			<div className="filters-section">
				<DropDown
					options={OrdersHistoryStatuses}
					defaultSelectedOptions={initiallySelectedStatuses.current}
					label="Status"
					setSelectedOptionsFromParent={setSelectedStatuses_handler}
					useCheckbox
				/>
				<DropDown
					options={OrdersHistoryTimeframe}
					defaultValue={timeFrameValue}
					label="Time Frame"
					setOptionValueFromParent={setTimeFrameValue_handler}
					autohide
				/>
				<div className="input-search-wrapper">
					<input
						className="input-search"
						onChange={setSearchValue_handler}
						value={searchValue}
						onFocus={() => setIsSearchInputOnFocus(true)}
						onBlur={() => setIsSearchInputOnFocus(false)}
						placeholder={!isSearchInputOnFocus && !searchValue ? "Search" : ""}
					/>
					<SvgIcon
						component={SearchIcon}
						width="16"
						height="16"
						className="search-icon"
					/>
					{searchValue && (
						<SvgIcon
							component={ClearBtn}
							width="20"
							height="20"
							className="input-clear-icon"
							onClick={clearSearchInput}
						/>
					)}
				</div>
				{!vendorStore.vendorDetails?.report_emails?.length ? null : (
					<SendHistoryReportButton
						status_names={selectedStatuses.map((s) => s.status!)}
						timeFrameValue={timeFrameValue}
					/>
				)}
			</div>

			{pullingOrdersHistoryInProgress ? (
				<Loading />
			) : !visibleOrders.length ? (
				<NoOrdersBasedOnFilters subtitle="Try changing filtering settings" />
			) : (
				<>
					<div className="orders-description">
						<div className="total-orders">{`${vendorStore.historyOrdersTotal} orders`}</div>
						<div className="pagination">
							<button
								className={`btn-pagination ${
									currentPage == 1 ? "disabled" : ""
								}`}
								disabled={currentPage == 1}
								onClick={() => setCurrentPage(1)}
							>
								First
							</button>
							<button
								className={`btn-pagination ${
									currentPage == 1 ? "disabled" : ""
								}`}
								disabled={currentPage == 1}
								onClick={() => setCurrentPage(currentPage - 1)}
							>
								{"<<"}
							</button>
							<span style={{ margin: 10 }}>
								{currentPage} / {vendorStore.historyPagesTotal}
							</span>
							<button
								className={`btn-pagination ${
									currentPage == vendorStore.historyPagesTotal ? "disabled" : ""
								}`}
								disabled={currentPage == vendorStore.historyPagesTotal}
								onClick={() => setCurrentPage(currentPage + 1)}
							>
								{">>"}
							</button>
							<button
								className={`btn-pagination ${
									currentPage == vendorStore.historyPagesTotal ? "disabled" : ""
								}`}
								disabled={currentPage == vendorStore.historyPagesTotal}
								onClick={() => setCurrentPage(vendorStore.historyPagesTotal)}
							>
								Last
							</button>
						</div>
					</div>
					<div
						className="table-wrapper"
						style={
							pullingOrdersHistoryInProgress
								? { width: "80%" }
								: { width: "100%" }
						}
					>
						<div className="tr thd">
							{headerOptions.map((option) => (
								<div
									key={option}
									className={`th ${
										option.trim() ? option.replace(/ /g, "") : "empty"
									}`}
								>
									{option}
								</div>
							))}
						</div>
						<div className="tbody">
							{pullingOrdersHistoryInProgress ? (
								<Loading />
							) : (
								visibleOrders.map((order) => (
									<div key={order.id} className="tr">
										<div className="td date">
											{getFormatedDate(order.order_meta.createdAt)}
										</div>
										<div className="td order-number">{order.id}</div>
										<div className="td tx-number">
											{order.order_meta.magentoSaleId}
										</div>
										<div className="td client-name">
											{(order.shipping_details.user_details.firstName || "") +
												" " +
												(order.shipping_details.user_details.lastName || "")}
										</div>
										<div className="td items">
											{getTotalNumberOfProducts(order)}
										</div>
										<div
											className={classNames("td tracking-number", {
												["not-available"]: !order.trackingNumber,
											})}
										>
											{order.trackingNumber || "-"}
										</div>
										<div className="td status">
											<span className="status-label">
												{orderStatusToLabel(order.status)}
											</span>
											{isPartialPickup(order) ? (
												<div className="pk-tag">PK</div>
											) : null}
										</div>
										<div className="td scheduled-delivery-date">
											{formatDeliveryDate(order.dates?.scheduled_date) || "-"}
										</div>
										<div className="td actual-delivery-date">
											{formatDeliveryDate(order.dates?.actual_date) || "-"}
										</div>
										<div className="td empty">
											<button
												className="table-details-button"
												onClick={(e) => openOrderCardModal(e as any, order)}
											>
												Details
											</button>
										</div>
										<div className="td empty">
											<button
												className={classNames("table-print-button", {
													["disabled"]: !order.trackingNumber,
												})}
												onClick={(e) => handlePrintBtn(e as any, order)}
											>
												Print
											</button>
										</div>
									</div>
								))
							)}
						</div>
					</div>
				</>
			)}
		</div>
	);
});

OrdersHistory.displayName = "OrdersHistory";
